import React, { useState, useEffect } from 'react';
import { API } from '../App';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Clock, Calendar as CalendarIcon } from 'lucide-react';

const CalendarView = () => {
  const [date, setDate] = useState(new Date());
  const [entries, setEntries] = useState([]);
  const [selectedDayEntries, setSelectedDayEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchEntries();
  }, [date]);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
      const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      
      const response = await axios.get(`${API}/time-entries`, {
        params: {
          start_date: format(startOfMonth, 'yyyy-MM-dd'),
          end_date: format(endOfMonth, 'yyyy-MM-dd')
        }
      });
      
      setEntries(response.data);
      updateSelectedDayEntries(date, response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Einträge');
    } finally {
      setLoading(false);
    }
  };

  const updateSelectedDayEntries = (selectedDate, allEntries) => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const dayEntries = allEntries.filter(e => e.date === dateStr);
    setSelectedDayEntries(dayEntries);
  };

  const handleDateSelect = (newDate) => {
    setDate(newDate);
    updateSelectedDayEntries(newDate, entries);
  };

  const getDayTotalHours = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    const dayEntries = entries.filter(e => e.date === dateStr && e.status === 'completed');
    return dayEntries.reduce((sum, e) => sum + (e.total_hours || 0), 0);
  };

  const modifiers = {
    hasEntries: (day) => {
      const dateStr = format(day, 'yyyy-MM-dd');
      return entries.some(e => e.date === dateStr);
    }
  };

  const modifiersStyles = {
    hasEntries: {
      backgroundColor: '#dbeafe',
      fontWeight: 'bold'
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="calendar-view">
        <div>
          <h1 className="text-4xl font-bold text-gray-900">Kalender</h1>
          <p className="text-gray-600 mt-2">Ihre Zeiterfassung im Überblick</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <Card className="lg:col-span-2 card-hover">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5" />
                Kalender
              </CardTitle>
            </CardHeader>
            <CardContent className="flex justify-center">
              <Calendar
                mode="single"
                selected={date}
                onSelect={handleDateSelect}
                locale={de}
                modifiers={modifiers}
                modifiersStyles={modifiersStyles}
                className="rounded-md border"
                data-testid="calendar-component"
              />
            </CardContent>
          </Card>

          {/* Selected Day Details */}
          <Card className="card-hover">
            <CardHeader>
              <CardTitle>Details für {format(date, 'dd.MM.yyyy', { locale: de })}</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : selectedDayEntries.length > 0 ? (
                <div className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Gesamtstunden</p>
                    <p className="text-3xl font-bold text-blue-600">
                      {selectedDayEntries
                        .filter(e => e.status === 'completed')
                        .reduce((sum, e) => sum + (e.total_hours || 0), 0)
                        .toFixed(2)}h
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    {selectedDayEntries.map((entry) => (
                      <div key={entry.id} className="bg-gray-50 p-3 rounded-lg" data-testid={`calendar-entry-${entry.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-gray-600" />
                            <span className="text-sm font-medium">
                              {format(new Date(entry.start_time), 'HH:mm', { locale: de })} - 
                              {entry.end_time ? format(new Date(entry.end_time), 'HH:mm', { locale: de }) : 'Aktiv'}
                            </span>
                          </div>
                          {entry.status === 'completed' && (
                            <span className="text-sm font-bold text-blue-600">
                              {entry.total_hours?.toFixed(2)}h
                            </span>
                          )}
                        </div>
                        {entry.activity_description && (
                          <p className="text-sm text-gray-600 mt-1">{entry.activity_description}</p>
                        )}
                        {entry.notes && (
                          <p className="text-xs text-gray-500 mt-1 italic">{entry.notes}</p>
                        )}
                        {entry.breaks && entry.breaks.length > 0 && (
                          <div className="mt-2 flex gap-1 flex-wrap">
                            {entry.breaks.map((b, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">
                                Pause {b.duration_minutes?.toFixed(0)}min
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">Keine Einträge für diesen Tag</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Monthly Summary */}
        <Card className="card-hover">
          <CardHeader>
            <CardTitle>Monatliche Übersicht - {format(date, 'MMMM yyyy', { locale: de })}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Gesamtstunden</p>
                <p className="text-2xl font-bold text-blue-600">
                  {entries
                    .filter(e => e.status === 'completed')
                    .reduce((sum, e) => sum + (e.total_hours || 0), 0)
                    .toFixed(2)}h
                </p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Arbeitstage</p>
                <p className="text-2xl font-bold text-green-600">
                  {new Set(entries.filter(e => e.status === 'completed').map(e => e.date)).size}
                </p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Durchschnitt/Tag</p>
                <p className="text-2xl font-bold text-purple-600">
                  {(() => {
                    const days = new Set(entries.filter(e => e.status === 'completed').map(e => e.date)).size;
                    const total = entries
                      .filter(e => e.status === 'completed')
                      .reduce((sum, e) => sum + (e.total_hours || 0), 0);
                    return days > 0 ? (total / days).toFixed(2) : '0.00';
                  })()}h
                </p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Einträge</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {entries.filter(e => e.status === 'completed').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default CalendarView;
